import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { useFetchVisibleProductsQuery } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";

import { createDevice } from "@dashboard/devices/store";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { Box, Typography } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { LoadingBox } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";

import { useStyles } from "./AddDeviceModal.styles";
import { schema, useDevices } from "./AddDeviceModal.utils";
import { AddedDevice, AddedDeviceType } from "./AddedDevice";
import { QrScanner } from "./QrScanner";

export type FormValues = {
    androidSerial: string;
    productName: string;
    groupId: number;
};

type Props = {
    open: boolean;
    onClose: () => void;
};

export const AddDeviceModal = (props: Props) => {
    const { addedDevices, setAddedDevices, devices } = useDevices();

    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();

    const visibleProductsResult = useFetchVisibleProductsQuery(undefined, { skip: !props.open });
    const { data: visibleProducts = [], isLoading } = visibleProductsResult;
    useQueryErrorToast(visibleProductsResult);
    const productOptions = _.orderBy(
        visibleProducts.map((product) => ({
            label: product.productName,
            value: product.codename,
        })),
        "label",
    );

    const groups = useSelector(selectGroups);
    const groupOptions = _.orderBy(
        groups.map((group) => ({
            label: group.name,
            value: group.id,
        })),
        "label",
    );

    const onValidate = async (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (values.androidSerial) {
            const serialNumbers = values.androidSerial.split(",").map((s) => s.trim());
            const deviceSerials = new Set(devices.current.map((device) => device.serial));
            const alreadyExists = serialNumbers.find((serial) => deviceSerials.has(serial));
            if (alreadyExists) {
                errors.androidSerial = t("modals:addDevice.toast.deviceAlreadyExists");
            }
        }
        if (values.androidSerial !== undefined) {
            const result = schema.safeParse(values);
            if (!result.success) {
                result.error.issues.forEach((issue) => {
                    if (issue.path.length > 0) {
                        const fieldName = issue.path[0] as keyof FormValues;
                        errors[fieldName] = issue.message;
                    }
                });
            }
        }

        return errors;
    };
    const onCreateDevice = async (values: FormValues) => {
        const serialNumbers = (values.androidSerial || "")
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

        const newAddedDevices = await serialNumbers.reduce<Promise<AddedDeviceType[]>>(async (accPromise, serialNumber) => {
            const acc = await accPromise;
            try {
                await dispatch(
                    createDevice({
                        productName: values.productName,
                        groupId: values.groupId,
                        androidSerial: serialNumber,
                    }),
                ).unwrap();
                acc.push({
                    ...values,
                    groupId: values.groupId,
                    androidSerial: serialNumber,
                    error: null,
                    createdAt: new Date().toISOString(),
                });
            } catch (error) {
                const err = error as Error;
                acc.push({
                    ...values,
                    groupId: values.groupId,
                    androidSerial: serialNumber,
                    error: err.message || t("modals:addDevice.toast.deviceCreationFailed"),
                    createdAt: new Date().toISOString(),
                });
            }
            return acc;
        }, Promise.resolve([]));

        return newAddedDevices;
    };
    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            const newAddedDevices = await onCreateDevice(values);
            setAddedDevices(newAddedDevices);

            const hasErrors = newAddedDevices.some((device) => device.error);
            if (hasErrors) {
                throw new Error();
            }

            showSuccessToast(t("modals:addDevice.toast.deviceCreatedSuccess"));
            form.restart();
        } catch (error) {
            const err = error as Error;
            const message = err.message || t("modals:addDevice.toast.deviceCreationFailed");
            showErrorToast(message);
        }
    };
    const onClose = (form: FormApi<FormValues>) => {
        setAddedDevices([]);
        form.restart();
        props.onClose();
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            validateOnBlur={true}
            initialValues={{
                androidSerial: "",
                productName: productOptions[0]?.value,
                groupId: groupOptions[0]?.value,
            }}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    maxWidth="md"
                    open={props.open}
                    onClose={() => onClose(form)}
                    title={t("modals:addDevice.title")}
                    description={t("modals:addDevice.description")}
                >
                    {isLoading ? (
                        <Box className={classes.loadingBox}>
                            <LoadingBox />
                        </Box>
                    ) : (
                        <Box className={classes.modalContent}>
                            <form onSubmit={handleSubmit} className={classes.form}>
                                <ControlledSelectField
                                    name="productName"
                                    label={t("common:label.product")}
                                    options={productOptions}
                                    searchable
                                />
                                <ControlledSelectField
                                    name="groupId"
                                    label={t("common:label.group")}
                                    options={groupOptions}
                                    searchable
                                />
                                <TextField name="androidSerial" label={t("modals:addDevice.input.androidSerial.caption")} />
                                <Box className={classes.formButtonContainer}>
                                    <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                        {t("common:button.enroll")}
                                    </PrimaryButton>
                                </Box>
                                <QrScanner />
                            </form>
                            <Box className={classes.devicesList}>
                                {addedDevices.length ? (
                                    addedDevices.map((device) => <AddedDevice key={device.androidSerial} {...device} />)
                                ) : (
                                    <Box className={classes.emptyDevicesList}>
                                        <Typography>{t("modals:addDevice.noDevices")}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Modal>
            )}
        />
    );
};
